import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  ButtonSpinner,
  Icon,
  InputText,
  InputCheckbox,
} from "@sellar-io/components";
import { useEffect, useEffectEvent, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { pluralize } from "@sellar-io/common";
import { useCustomMultiSelectCombobox } from "./useCustomMultiSelectCombobox";

export type CustomMultiSelectComboboxOption = {
  id: number;
  name: string;
};

type CustomMultiSelectComboboxProps = {
  singleItemLabel: string;
  value: CustomMultiSelectComboboxOption[];
  options: CustomMultiSelectComboboxOption[];
  placeholder: string;
  inputPlaceholder: string;
  searchQuery: string;
  isLoading?: boolean;
  filterSlot?: ({
    onChange,
    value,
  }: {
    onChange: (value: CustomMultiSelectComboboxOption[]) => void;
    value: CustomMultiSelectComboboxOption[];
  }) => ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  onChange: (value: CustomMultiSelectComboboxOption[]) => void;
  onSearch: (query: string) => void;
};

const CustomMultiSelectComboboxPanel = ({
  singleItemLabel,
  value,
  options,
  inputPlaceholder,
  searchQuery,
  isLoading = false,
  filterSlot,
  onChange,
  onSearch,
}: Omit<CustomMultiSelectComboboxProps, "placeholder" | "onOpenChange">) => {
  const {
    query,
    selectedOptions,
    unselectedOptions,
    handleSearchChange,
    getToggledValue,
  } = useCustomMultiSelectCombobox({
    selection: value,
    options,
    onSearch,
  });

  const toggleOption = (option: CustomMultiSelectComboboxOption) => {
    onChange(getToggledValue(option));
  };

  return (
    <>
      <div className="relative border-b border-solid border-zinc-300 p-2">
        <InputText
          type="text"
          placeholder={inputPlaceholder}
          value={query}
          onChange={handleSearchChange}
          className="h-10 w-full rounded-md border border-solid border-zinc-300 bg-white px-3 text-sm outline-none placeholder:text-zinc-500 focus:border-zinc-900"
        />
      </div>
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <ButtonSpinner className="text-zinc-500" />
        </div>
      )}
      {!isLoading && options.length === 0 && query.length > 0 && (
        <div className="px-3 py-4 text-sm text-zinc-500">
          No {pluralize(singleItemLabel, 2)} found
        </div>
      )}
      {!searchQuery && filterSlot && (
        <>
          {filterSlot({ onChange, value })} <hr />
        </>
      )}

      <ul
        role="listbox"
        aria-multiselectable="true"
        className="flex max-h-64 flex-col overflow-auto"
      >
        {selectedOptions.length > 0 && (
          <>
            <div className="px-3 py-2 text-xs font-semibold tracking-wide text-zinc-400 uppercase">
              Current Selection
            </div>
            {selectedOptions.map((option) => {
              const isSelected = value.some((item) => item.id === option.id);
              return (
                <li
                  key={option.id}
                  role="option"
                  aria-selected={isSelected}
                  className="group flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-zinc-900 hover:bg-zinc-50"
                  onClick={() => toggleOption(option)}
                >
                  <InputCheckbox
                    checked={isSelected}
                    readOnly
                    className="h-4 w-4 rounded border-zinc-300 text-zinc-900 accent-[#A34FD2] focus:ring-zinc-900"
                  />
                  <span className="flex-1">{option.name}</span>
                </li>
              );
            })}
          </>
        )}
        <>
          {searchQuery && unselectedOptions.length > 0 && (
            <div className="px-3 py-2 text-xs font-semibold tracking-wide text-zinc-400 uppercase">
              Search Results
            </div>
          )}
          {unselectedOptions.map((option) => {
            const isSelected = value.some((item) => item.id === option.id);
            return (
              <li
                key={option.id}
                role="option"
                aria-selected={isSelected}
                className="group flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-zinc-900 hover:bg-zinc-50"
                onClick={() => toggleOption(option)}
              >
                <InputCheckbox
                  checked={isSelected}
                  readOnly
                  className="h-4 w-4 rounded border-zinc-300 text-zinc-900 accent-[#A34FD2] focus:ring-zinc-900"
                />
                <span className="flex-1">{option.name}</span>
              </li>
            );
          })}
        </>
      </ul>
      {value.length > 0 && (
        <div className="flex items-center gap-2 border-t border-zinc-300 p-2 text-sm text-[#8C23C7]">
          <span>{value.length}</span>{" "}
          <span>{pluralize(singleItemLabel, value.length)}</span>
          selected
        </div>
      )}
    </>
  );
};

export const CustomMultiSelectCombobox = ({
  singleItemLabel,
  value,
  options,
  placeholder,
  inputPlaceholder,
  searchQuery,
  isLoading = false,
  filterSlot,
  onChange,
  onSearch,
  onOpenChange,
}: CustomMultiSelectComboboxProps) => {
  const displayLabel = value.length
    ? `${value.length} ${pluralize(singleItemLabel, value.length)} selected`
    : placeholder;

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange?.(isOpen);
    if (!isOpen) {
      onSearch("");
    }
  };

  return (
    <Popover as="div" className="relative">
      {({ open }) => {
        return (
          <>
            <PopoverController open={open} onOpenChange={handleOpenChange} />
            <PopoverButton className="flex h-10 w-full items-center justify-between rounded-md border border-solid border-zinc-300 bg-white px-3 text-left text-sm outline-none focus:border-zinc-900">
              <span
                className={value.length ? "text-zinc-900" : "text-zinc-500"}
              >
                {displayLabel}
              </span>
              <Icon
                icon="CHEVRON_DOWN"
                className={twMerge(
                  "text-zinc-500 transition-transform",
                  open && "rotate-180",
                )}
              />
            </PopoverButton>

            <PopoverPanel
              transition
              className="absolute top-full left-0 z-50 mt-1 flex w-full flex-col gap-2 rounded-md border border-solid border-zinc-200 bg-white shadow-lg"
            >
              <CustomMultiSelectComboboxPanel
                key={open ? "open" : "closed"}
                singleItemLabel={singleItemLabel}
                value={value}
                options={options}
                inputPlaceholder={inputPlaceholder}
                searchQuery={searchQuery}
                isLoading={isLoading}
                filterSlot={filterSlot}
                onChange={onChange}
                onSearch={onSearch}
              />
            </PopoverPanel>
          </>
        );
      }}
    </Popover>
  );
};

const PopoverController = ({
  open,
  onOpenChange,
}: Pick<CustomMultiSelectComboboxProps, "onOpenChange"> & {
  open: boolean;
}) => {
  const notifyOpenChange = useEffectEvent((isOpen: boolean) => {
    onOpenChange?.(isOpen);
  });

  useEffect(() => {
    notifyOpenChange(open);
  }, [open]);

  return null;
};
